import EngineeringIcon from "@mui/icons-material/Engineering";
import StoreIcon from "@mui/icons-material/Store";
import { Alert, Box, Button, Stack, Typography } from "@mui/material";
import type { MyInvitationDto } from "Infrastructure/Api/Api";
import { AccessRoleCodes } from "Models/AccessRoleCodes";
import { Resources, useResource } from "Translations/Resources";
import { formatDate } from "Utils/DateUtils";

const COMPONENT_RESOURCES = Resources.Auth.Invitations;

type Props = {
  invitation: MyInvitationDto;
  isAccepting: boolean;
  isDeclining: boolean;
  onAccept: (publicID: string) => void;
  onDecline: (publicID: string) => void;
};

/**
 * Single invitation row in the inbox. Displays the role-grant context (today only Merchant —
 * company name on top) plus inviter + expiry. Accept is disabled when the server reports the
 * invitation isn't acceptable (e.g. Identity isn't FullMoone yet); a banner explains why.
 */
export const InvitationCard = ({
  invitation: {
    publicID,
    accessRoleCode,
    merchantCompanyName,
    inviterDisplayName,
    expiresAt,
    isAcceptable,
  },
  isAccepting,
  isDeclining,
  onAccept,
  onDecline,
}: Props) => {
  const { t } = useResource();

  const isEmployee = accessRoleCode === AccessRoleCodes.Employee;

  const title = isEmployee
    ? t(COMPONENT_RESOURCES.EmployeeInviteTitle, {
        companyName: merchantCompanyName ?? "",
      })
    : merchantCompanyName
      ? t(COMPONENT_RESOURCES.MerchantInviteTitle, {
          companyName: merchantCompanyName,
        })
      : t(COMPONENT_RESOURCES.MerchantInviteUnnamedCompany);

  return (
    <Box
      borderRadius={2}
      bgcolor="background.paper"
      border={1}
      borderColor="divider"
      p={3}
    >
      <Stack gap={2}>
        <Stack direction="row" gap={2} alignItems="flex-start">
          {isEmployee ? (
            <EngineeringIcon color="primary" />
          ) : (
            <StoreIcon color="primary" />
          )}
          <Stack gap={0.5} flex={1}>
            <Typography variant="body1" fontWeight={600}>
              {title}
            </Typography>
            {inviterDisplayName && (
              <Typography variant="body2" color="text.secondary">
                {t(COMPONENT_RESOURCES.InvitedBy, {
                  inviter: inviterDisplayName,
                })}
              </Typography>
            )}
            <Typography variant="caption" color="text.secondary">
              {t(COMPONENT_RESOURCES.ExpiresAt, {
                date: formatDate(expiresAt),
              })}
            </Typography>
          </Stack>
        </Stack>

        {!isAcceptable && (
          <Alert severity="warning" variant="outlined">
            {t(COMPONENT_RESOURCES.KycRequired)}
          </Alert>
        )}

        <Stack direction="row" gap={1} justifyContent="flex-end">
          <Button
            variant="text"
            color="inherit"
            onClick={() => onDecline(publicID)}
            disabled={isAccepting}
            loading={isDeclining}
          >
            {t(COMPONENT_RESOURCES.DeclineButton)}
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={() => onAccept(publicID)}
            disabled={isDeclining || !isAcceptable}
            loading={isAccepting}
          >
            {t(COMPONENT_RESOURCES.AcceptButton)}
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
};
